import {
  AlertTriangle,
  ClipboardIcon,
  Copy,
  EditIcon,
  Share2,
  ThumbsDown,
  ThumbsUp,
  Undo,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import TextareaAutosize from "react-textarea-autosize";
import { useEditorPanel } from "../hooks/useEditorPanel";
import { useIsMobile } from "../hooks/useIsMobile";
import { DirectionSwitcher } from "./DirectionSwitcher";
import { FeedbackModal } from "./FeedbackModal";
import { PremiumModal } from "./PremiumModal";
import { ReportModal } from "./ReportModal";
import { ShareModal } from "./ShareModal";
import { Button } from "~/components/ui/button";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "~/components/ui/tooltip";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
} from "~/components/ui/dialog";

interface EditorPanelProps {
  inputText: string;
  setInputText: (text: string) => void;
  setOutputText: (text: string) => void;
  outputText: string;
  handleTranslit: () => void;
  selectedDirection: string;
  onFromLanguageChange: (key: string) => void;
  onToLanguageChange: (key: string) => void;
  onDirectionSwap: () => void;
  readOnly?: boolean;
}

export function EditorPanel(props: EditorPanelProps) {
  const { t } = useTranslation();
  const {
    textareaRef,
    specialChars,
    hasInput,
    canUndoClear,
    inputLength,
    isOverLimit,
    maxChars,
    readOnly,
    handleClearInput,
    handleUndo,
    handleSpecialCharClick,
    handlePasteInput,
    handleCopyOutput,
    showCopied,
    setShowCopied,
    handleShare,
    shareData,
    shareLoading,
    clearShareData,
    quickRating,
    setQuickRating,
    handleQuickLike,
    updateSelectionState,
  } = useEditorPanel(props);

  const isMobile = useIsMobile();

  const {
    inputText,
    setInputText,
    outputText,
    selectedDirection,
    onFromLanguageChange,
    onToLanguageChange,
    onDirectionSwap,
  } = props;

  // Word popover state
  const [popover, setPopover] = useState<{
    word: string;
    top: number;
    left: number;
  } | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Report modal state
  const [reportWord, setReportWord] = useState("");
  const [reportOpen, setReportOpen] = useState(false);

  const handleWordClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>, word: string) => {
      setPopover({
        word,
        top: e.clientY + 10,
        left: e.clientX + 10,
      });
    },
    []
  );

  const handleReportClick = useCallback(() => {
    if (popover) {
      setReportWord(popover.word);
      setPopover(null);
      setReportOpen(true);
    }
  }, [popover]);

  // Close popover on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setPopover(null);
      }
    };
    if (popover) {
      document.addEventListener("mousedown", handleClick);
    }
    return () => document.removeEventListener("mousedown", handleClick);
  }, [popover]);

  return (
    <div className="flex-1 min-w-0 flex divide-y lg:divide-x bg-white flex-col lg:flex-row">
      {/* Direction switcher - visible only on mobile */}
      <DirectionSwitcher
        selectedDirection={selectedDirection}
        onFromLanguageChange={onFromLanguageChange}
        onToLanguageChange={onToLanguageChange}
        onDirectionSwap={onDirectionSwap}
        readOnly={readOnly}
        className="p-4 mb-0 lg:hidden"
      />
      <div className="flex-1 min-w-0 flex relative flex-col lg:flex-row ">
        {isMobile ? (
          <TextareaAutosize
            ref={textareaRef as any}
            value={inputText}
            readOnly={readOnly}
            onChange={(e) => {
              if (readOnly) return;
              setInputText(e.target.value);
            }}
            onSelect={updateSelectionState}
            onKeyUp={updateSelectionState}
            onMouseUp={updateSelectionState}
            placeholder={t("editor.inputPlaceholder")}
            aria-label={t("editor.inputPlaceholder")}
            minRows={3}
            className="w-full p-4 pr-18 resize-none focus:outline-hidden"
          />
        ) : (
          <textarea
            ref={textareaRef}
            value={inputText}
            readOnly={readOnly}
            onChange={(e) => {
              if (readOnly) return;
              setInputText(e.target.value);
            }}
            onSelect={updateSelectionState}
            onKeyUp={updateSelectionState}
            onMouseUp={updateSelectionState}
            placeholder={t("editor.inputPlaceholder")}
            aria-label={t("editor.inputPlaceholder")}
            className="w-full p-4 lg:pb-32 pr-18 resize-none focus:outline-hidden"
          />
        )}
        <div
          className={
            hasInput || canUndoClear
              ? "absolute top-4 right-4"
              : "absolute top-4 right-4 hidden"
          }
        >
          {(hasInput || canUndoClear) && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClearInput}
              disabled={readOnly}
              aria-label={t("aria.clearInput")}
            >
              <X />
            </Button>
          )}
          {canUndoClear && (
            <Button variant="ghost" size="icon" onClick={handleUndo} disabled={readOnly} aria-label={t("aria.undo")}>
              <Undo />
            </Button>
          )}
        </div>
        <div className="lg:absolute bottom-0 left-0 flex justify-between gap-2 p-4 lg:pt-12 right-0 from-transparent from-0% to-white to-40% bg-linear-to-b">
          <div className="flex">
            {specialChars.map((char) => (
              <Button
                key={char}
                variant="ghost"
                size="icon"
                onClick={() => handleSpecialCharClick(char)}
                disabled={readOnly}
              >
                {char}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              {inputLength > 0 && (
                <div className={isOverLimit ? "text-red-600" : "text-gray-400"}>
                  {inputLength} / {maxChars}
                </div>
              )}
              {isOverLimit && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-800 rounded-lg px-3 py-1 font-bold text-xs text-white hover:bg-blue-900">
                      {t("editor.premium")}
                    </Button>
                  </DialogTrigger>
                  <PremiumModal />
                </Dialog>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePasteInput}
              disabled={readOnly}
              aria-label={t("aria.paste")}
            >
              <ClipboardIcon />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 min-w-0 flex relative">
        <div
          role="textbox"
          aria-readonly="true"
          aria-label={t("editor.outputPlaceholder")}
          aria-placeholder={t("editor.outputPlaceholder")}
          className="w-full p-4 pb-24 pr-18 resize-none focus:outline-hidden whitespace-pre-wrap break-words overflow-y-auto"
        >
          {outputText ? (
            outputText.split(/(\s+)/).map((word, index) => {
              if (/^\s+$/.test(word)) {
                return <React.Fragment key={index}>{word}</React.Fragment>;
              }
              return word ? (
                <span
                  key={index}
                  className="cursor-pointer hover:bg-blue-50 rounded"
                  onClick={(e) => handleWordClick(e, word)}
                >
                  {word}
                </span>
              ) : null;
            })
          ) : (
            <span>{t("editor.outputPlaceholder")}</span>
          )}
        </div>

        {/* Word error popover */}
        {popover && (
          <div
            ref={popoverRef}
            className="fixed z-30 shadow-lg rounded-lg border border-slate-300 bg-white"
            style={{ top: popover.top, left: popover.left }}
          >
            <button
              type="button"
              className="flex items-center gap-2 cursor-pointer rounded-lg p-2 px-4 hover:bg-slate-50 text-sm"
              onClick={handleReportClick}
            >
              <AlertTriangle size={18} className="text-red-600" />
              {t("report.button")}
            </button>
          </div>
        )}

        {/* Report modal */}
        <ReportModal
          word={reportWord}
          inputText={inputText}
          outputText={outputText}
          isOpen={reportOpen}
          onClose={() => setReportOpen(false)}
        />
        <div className="absolute bottom-0 left-0 flex justify-between gap-2 p-4 pt-12 right-0 from-transparent from-0% to-white to-40% bg-linear-to-b">
          <div className="flex">
            <div className="flex">
              <Button variant="ghost" size="icon" disabled aria-label={t("aria.edit")}>
                <EditIcon />
              </Button>
              {inputText.trim() && outputText.trim() && (
                <Dialog
                  open={!!shareData}
                  onOpenChange={(isOpen) => {
                    if (!isOpen) {
                      clearShareData();
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleShare}
                      disabled={readOnly || shareLoading}
                      aria-label={t("aria.share")}
                    >
                      <Share2 />
                    </Button>
                  </DialogTrigger>
                  {shareData && (
                    <ShareModal
                      shareCode={shareData.share_code}
                      shareUrl={shareData.share_url}
                      onClose={clearShareData}
                    />
                  )}
                </Dialog>
              )}
              <Button
                variant="ghost"
                size="icon"
                className={quickRating === "like" ? "text-green-500" : ""}
                onClick={handleQuickLike}
                disabled={readOnly}
                aria-label={t("aria.like")}
              >
                <ThumbsUp />
              </Button>
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={quickRating === "dislike" ? "text-red-500" : ""}
                    onClick={() => setQuickRating("dislike")}
                    disabled={readOnly}
                    aria-label={t("aria.feedback")}
                  >
                    <ThumbsDown />
                  </Button>
                </DialogTrigger>
                <FeedbackModal inputText={inputText} outputText={outputText} />
              </Dialog>
            </div>
          </div>
          <div className="flex">
            <Tooltip open={showCopied}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    handleCopyOutput();
                    setShowCopied(true);
                    setTimeout(() => setShowCopied(false), 2000);
                  }}
                  aria-label={t("aria.copy")}
                >
                  <Copy />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("editor.copied")}</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
}
